import React, { useEffect, useState } from "react";
import MessengerLayout from "../../layout/MessengerLayout";
import { toast } from "../../components/ui/Toast";

const API = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card  = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const input = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50";
const label = "text-xs text-[#00ff90]/60 uppercase tracking-wider block mb-1.5";
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" });

const STAGE_STYLE = {
  raised:        "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  acknowledged:  "text-blue-400 bg-blue-500/10 border-blue-500/20",
  investigating: "text-purple-400 bg-purple-500/10 border-purple-500/20",
  hearing:       "text-orange-400 bg-orange-500/10 border-orange-500/20",
  outcome:       "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  appeal:        "text-red-400 bg-red-500/10 border-red-500/20",
  closed:        "text-white/40 bg-white/5 border-white/10",
};

export default function GrievancePage() {
  const [user]   = useState(() => { try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; } });
  const isAdmin  = !!(user && (user.role === "admin" || user.role === "superadmin" || user.roles?.includes?.("admin")));
  const [tab, setTab]     = useState(isAdmin ? "all" : "mine");
  const [mine, setMine]   = useState([]);
  const [all, setAll]     = useState([]);
  const [draft, setDraft] = useState({ subject: "", description: "", is_confidential: true });
  const [busy, setBusy]   = useState(null);
  const [msg, setMsg]     = useState(null);

  const loadMine = async () => {
    const r = await fetch(`${API}/api/grievance/mine`, { headers: authH() });
    setMine(await r.json());
  };
  const loadAll = async () => {
    const r = await fetch(`${API}/api/grievance`, { headers: authH() });
    setAll(await r.json());
  };
  useEffect(() => { loadMine(); if (isAdmin) loadAll(); }, []);   // eslint-disable-line

  const raise = async () => {
    setBusy("raise"); setMsg(null);
    try {
      const r = await fetch(`${API}/api/grievance`, {
        method: "POST", headers: authH(),
        body: JSON.stringify({ ...draft, case_type: "grievance" }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      setMsg({ ok: true, text: "Case raised" });
      toast.success("Case raised");
      setDraft({ subject: "", description: "", is_confidential: true });
      loadMine();
    } catch (e) { setMsg({ ok: false, text: e.message }); toast.error(e.message || "Failed to raise case"); }
    finally { setBusy(null); }
  };

  const acknowledge = async (id) => {
    setBusy(id);
    try {
      const r = await fetch(`${API}/api/grievance/${id}/acknowledge`, { method: "PATCH", headers: authH() });
      if (!r.ok) throw new Error("Failed");
      toast.success("Grievance acknowledged");
      loadAll();
    } catch (e) {
      toast.error("Failed to acknowledge");
    } finally { setBusy(null); }
  };

  const advance = async (id, stage) => {
    setBusy(id);
    try {
      const r = await fetch(`${API}/api/grievance/${id}/stage`, {
        method: "PATCH", headers: authH(),
        body: JSON.stringify({ stage }),
      });
      if (!r.ok) throw new Error("Failed");
      if (stage === "investigating") toast.success("Investigation started");
      else if (stage === "outcome") toast.success("Outcome recorded");
      else toast.success("Stage updated");
      loadAll();
    } catch (e) {
      if (stage === "investigating") toast.error("Failed to update stage");
      else if (stage === "outcome") toast.error("Failed to record outcome");
      else toast.error("Failed to update stage");
    } finally { setBusy(null); }
  };

  const Row = ({ c, admin }) => (
    <div className={`${card} p-4`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-white/85 truncate">{c.subject}</p>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${STAGE_STYLE[c.stage] || "text-white/40 bg-white/5 border-white/10"}`}>
              {c.stage}
            </span>
            <span className="text-xs text-white/40 capitalize">· {c.case_type}</span>
            {c.is_confidential && <span className="text-xs px-1.5 py-0.5 rounded border border-yellow-500/30 bg-yellow-500/10 text-yellow-400">confidential</span>}
          </div>
          <p className="text-xs text-white/40 mt-1 line-clamp-2">{c.description}</p>
          <p className="text-xs text-white/30 mt-1">Raised: {c.created_at?.slice(0, 10)}</p>
        </div>
        {admin && (
          <div className="flex gap-2">
            {c.stage === "raised" && (
              <button onClick={() => acknowledge(c.id)} disabled={busy === c.id}
                className="text-xs px-3 py-1 rounded border border-blue-500/30 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20">Acknowledge</button>
            )}
            {c.stage === "acknowledged" && (
              <button onClick={() => advance(c.id, "investigating")} disabled={busy === c.id}
                className="text-xs px-3 py-1 rounded border border-purple-500/30 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20">Start investigation</button>
            )}
            {c.stage === "investigating" && (
              <button onClick={() => advance(c.id, "outcome")} disabled={busy === c.id}
                className="text-xs px-3 py-1 rounded border border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90] hover:bg-[#00ff90]/20">Record outcome</button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <MessengerLayout>
      <div className="max-w-4xl mx-auto p-6 space-y-5">
        <header className="space-y-1">
          <h1 className="text-xl font-semibold text-white">Grievance</h1>
          <p className="text-xs text-white/40">Raise concerns confidentially. HR will acknowledge within 48 hours.</p>
        </header>

        <div className="flex gap-2">
          <button onClick={() => setTab("mine")} className={`text-xs px-3 py-1.5 rounded-full border ${tab === "mine" ? "border-[#00ff90]/50 bg-[#00ff90]/10 text-[#00ff90]" : "border-white/10 text-white/50"}`}>My cases</button>
          <button onClick={() => setTab("new")}  className={`text-xs px-3 py-1.5 rounded-full border ${tab === "new"  ? "border-[#00ff90]/50 bg-[#00ff90]/10 text-[#00ff90]" : "border-white/10 text-white/50"}`}>Raise new</button>
          {isAdmin && <button onClick={() => setTab("all")} className={`text-xs px-3 py-1.5 rounded-full border ${tab === "all" ? "border-[#00ff90]/50 bg-[#00ff90]/10 text-[#00ff90]" : "border-white/10 text-white/50"}`}>All cases ({all.length})</button>}
        </div>

        {tab === "mine" && (
          <div className="space-y-2">
            {mine.length === 0 ? <p className="text-white/40 text-sm italic p-3">No cases yet.</p>
              : mine.map(c => <Row key={c.id} c={c} />)}
          </div>
        )}
        {tab === "new" && (
          <div className={`${card} p-5 space-y-3`}>
            <div><label className={label}>Subject</label>
              <input className={input} value={draft.subject} onChange={e => setDraft(d => ({ ...d, subject: e.target.value }))} /></div>
            <div><label className={label}>Description</label>
              <textarea rows={5} className={input} value={draft.description} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} /></div>
            <label className="flex items-center gap-2 text-xs text-white/70">
              <input type="checkbox" checked={draft.is_confidential}
                onChange={e => setDraft(d => ({ ...d, is_confidential: e.target.checked }))} />
              Keep confidential (manager won't see this case)
            </label>
            <button onClick={raise} disabled={busy === "raise" || !draft.subject || !draft.description}
              className="text-xs px-4 py-2 rounded-lg border border-[#00ff90]/40 bg-[#00ff90]/10 text-[#00ff90] hover:bg-[#00ff90]/20">
              {busy === "raise" ? "Submitting…" : "Raise grievance"}
            </button>
            {msg && <p className={`text-xs ${msg.ok ? "text-[#00ff90]" : "text-red-400"}`}>{msg.text}</p>}
          </div>
        )}
        {tab === "all" && isAdmin && (
          <div className="space-y-2">
            {all.length === 0 ? <p className="text-white/40 text-sm italic p-3">No cases yet.</p>
              : all.map(c => <Row key={c.id} c={c} admin />)}
          </div>
        )}
      </div>
    </MessengerLayout>
  );
}
